import {
  WhmFlexDataGrid,
  WhmBox,
  WhmCircularProgress,
} from "@securitize/reactjs-whm";
import {
  fundHoldingsByTokenIdRequest,
  useFundSchemaByTokenIdQuery,
} from "../../data/fundHoldingsRequest";
import { FundHolding } from "../../data/dtos/fund-holdings";
import { useContext, useEffect, useMemo, useState } from "react";
import { I18nContext } from "../../contexts/I18nContext";
import Snackbar, { SnackbarContext } from "../../components/Snackbar";
import ErrorSnackbar from "../../components/ErrorSnackbar";
import { ErrorPage } from "../../components/ErrorPage";
import { ResponseError } from "../../types/responseError";
import { FundHoldingsCardMobileComponent } from "../../views/FundHoldingDetails/CardMobileComponent";
import { buildColumns, ROWS_PER_PAGE_OPTIONS, valueFormatter } from "./utils";
import FundHoldingsDownloadModal from "../FundHoldingsDownloadModal";
import FundHoldingsTableToolbar from "./FundHoldingsTableToolbar";

export type FundHoldingDetailsProps = { tokenId: string };
export default function FundHoldingsTable({
  tokenId,
}: FundHoldingDetailsProps) {
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [sourceDate, setSourceDate] = useState<string | null>(null);
  const [errorData, setErrorData] = useState<ResponseError | null>(null);
  const { getTranslation } = useContext(I18nContext);
  const { showSnackbar } = useContext(SnackbarContext);
  const schemaQuery = useFundSchemaByTokenIdQuery({
    tokenId,
  });

  const generalError: ResponseError | null = useMemo(
    () => errorData || schemaQuery.error,
    [errorData, schemaQuery.error],
  );

  useEffect(() => {
    if (generalError) {
      showSnackbar({
        message: <ErrorSnackbar error={generalError} />,
        severity: "error",
      });
    }
  }, [generalError, getTranslation, showSnackbar]);

  const refetchAllData = async () => {
    if (errorData) {
      setErrorData(null);
      return;
    }

    await schemaQuery.refetch();
  };

  const handleDateChange = (date: string) => {
    setSourceDate(date);
    void refetchAllData();
  };

  return (
    <>
      {schemaQuery.isLoading && (
        <WhmBox
          sx={{
            display: "flex",
            justifyContent: "center",
            pt: "120px",
            pb: "120px",
          }}
        >
          <WhmCircularProgress />
        </WhmBox>
      )}
      {generalError && (
        <ErrorPage
          errorCode={generalError.correlationId}
          onRefresh={refetchAllData}
        />
      )}
      <FundHoldingsTableToolbar
        tokenId={tokenId}
        sourceDate={sourceDate}
        onDateChange={handleDateChange}
        onDownload={() => setIsDownloadModalOpen(true)}
      />
      {!schemaQuery.isLoading && schemaQuery.data && !generalError && (
        <>
          <WhmFlexDataGrid
            loadServerRows={async ({ page, pageSize }) => {
              let result: { data: FundHolding[]; total: number } = {
                data: [],
                total: 0,
              };

              try {
                result = await fundHoldingsByTokenIdRequest({
                  tokenId,
                  page: page + 1,
                  pageSize,
                  sourceDate: sourceDate ?? undefined,
                });
              } catch (e) {
                setErrorData(e as ResponseError);
                return { data: [], totalItems: 0 };
              }

              return {
                data: result.data.map((holding) => {
                  const dataToShow = schemaQuery.data
                    .map((schema) => {
                      const value = holding[schema.field as keyof FundHolding];

                      return {
                        [schema.field]: valueFormatter(value, schema.type),
                      };
                    })
                    .reduce((acc, curr) => ({ ...acc, ...curr }), {} as Record<string, unknown>);

                  return {
                    ...dataToShow,
                    // Ensure the ID is included in the data, id is mandatory for the grid
                    id: (dataToShow.id as string) || holding.id,
                    _originalHolding: holding,
                  };
                }),
                totalItems: result.total,
              };
            }}
            sx={{
              "&.MuiDataGrid-root": {
                border: "none",
              },
              "& .MuiDataGrid-columnSeparator": {
                display: "none",
              },
            }}
            CardComponent={({ row }) => {
              const originalHolding = (row as { _originalHolding?: FundHolding })?._originalHolding;
              return (
                <FundHoldingsCardMobileComponent
                  row={originalHolding || (row as unknown as FundHolding)}
                  fieldColumnsSchemas={schemaQuery.data}
                />
              );
            }}
            columns={buildColumns(schemaQuery.data, getTranslation)}
            columnsVisibility={{
              id: false,
              relatedWith: true,
            }}
            disableRowSelectionOnClick
            initialPage={0}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          />
        </>
      )}
      <FundHoldingsDownloadModal
        isOpen={isDownloadModalOpen}
        onClose={() => setIsDownloadModalOpen(false)}
        tokenId={tokenId}
        sourceDate={sourceDate}
      />
      <Snackbar />
    </>
  );
}
